import { AfterViewInit, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { UiAtomTextComponent } from '../../atoms/ui-atom-text/ui-atom-text.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const PARTNER_NAME_MAX = 50;

export interface PartnerUpsertData {
  title: string;
  message?: string;
  defaults?: {
    name?: string;
  };
  mode?: 'create' | 'edit';
  partner?: any;
  context?: 'global' | 'country';
  countryId?: string;
}

export interface PartnerDialogResult {
  mode: 'create' | 'edit';
  payload: any;
  partnerId?: string;
  context?: 'global' | 'country';
  countryId?: string;
}

@Component({
  selector: 'ui-molecule-dialog-partner-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    UiMoleculeInputComponent,
    UiAtomButtonComponent,
    UiAtomTextComponent,
    MatDialogClose,
    TranslatePipe,
  ],
  templateUrl: './ui-molecule-dialog-partner-upsert.component.html',
  styleUrl: './ui-molecule-dialog-partner-upsert.component.scss',
})
export class PartnerUpsertDialogComponent implements AfterViewInit {
  readonly nameMax = PARTNER_NAME_MAX;
  form: FormGroup;
  isLoading = false;
  errorMessage = '';
  formSubmitted = false;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<PartnerUpsertDialogComponent, PartnerDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PartnerUpsertData,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(PARTNER_NAME_MAX)]],
    });
  }

  get nameError(): string | null {
    const c = this.form.get('name');
    if (!c || (!c.touched && !c.dirty && !this.formSubmitted)) return null;
    if (c.hasError('required')) {
      return this.translate.instant('partners.form.name.error.required');
    }
    if (c.hasError('maxlength')) {
      return this.translate.instant('partners.form.name.error.maxLength', { max: this.nameMax });
    }
    return null;
  }

  ngAfterViewInit(): void {
    // Cargar datos por defecto o del partner a editar
    if (this.data.defaults) {
      this.form.patchValue(this.data.defaults);
    }

    if (this.data.mode === 'edit' && this.data.partner) {
      this.form.patchValue({
        name: this.data.partner.name,
      });
    }
  }

  submit(): void {
    this.formSubmitted = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const formData = this.form.getRawValue();

    if (this.data.mode === 'edit' && this.data.partner) {
      // Modo edición
      const payload = {
        name: formData.name,
      };
      this.dialogRef.close({
        mode: 'edit',
        payload: payload,
        partnerId: this.data.partner.id,
        context: this.data.context,
        countryId: this.data.countryId,
      });
    } else {
      // Modo creación
      // Para contexto country, el endpoint es POST /countries/{countryId}/partners
      // Solo se envía { name: "string" }, el countryId va en la URL
      const payload: any = {
        name: formData.name,
      };

      this.dialogRef.close({
        mode: 'create',
        payload: payload,
        context: this.data.context,
        countryId: this.data.countryId,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
